// maths.portll.net — FREE PLAY (BETA). Public, no account: mounts the offline maths game
// with a local-only anonymous profile. No Firestore write (no uid/childId), local BKT only.
// A small "Sign in to save progress" corner links parents to the account flow.
import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { resolve as resolveTheme } from "../lib/theme.js";
import { getVoice } from "../lib/voice.js";

const SCRIPT_ID = "maths-game-script";

function ensureGameScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.MathsGame) return resolve();
    let s = document.getElementById(SCRIPT_ID);
    if (s) {
      if (window.MathsGame) return resolve();
      s.addEventListener("load", () => resolve(), { once: true });
      s.addEventListener("error", reject, { once: true });
      return;
    }
    s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "/game.js";
    s.async = true;
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", reject, { once: true });
    document.body.appendChild(s);
  });
}

export default function FreePlay() {
  const ref = useRef(null);
  const liveRef = useRef(null);
  const { skillId } = useParams();   // /play/:skillId → boot straight into this game (undefined on /play → map)
  useEffect(() => {
    let cancelled = false;
    const host = ref.current;
    if (!host) return;
    ensureGameScript()
      .then(() => {
        if (cancelled || !window.MathsGame) return;
        // sync the canvas engine to the resolved scheme BEFORE mount (no dark flash in a light session).
        try {
          window.DNF && window.DNF.theme && window.DNF.theme.setScheme(resolveTheme());
        } catch (e) {}
        // no uid / no writeMastery → the engine runs local-only (BKT in memory/IndexedDB),
        // nothing is written to Firestore. A fixed local childId keeps one free-play profile.
        window.MathsGame.mount(host, { freePlay: true, childId: "freeplay", yearLevel: 0, displayName: "", skill: skillId, voice: getVoice(), announce: (t) => { if (liveRef.current) liveRef.current.textContent = t; } });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { window.MathsGame && window.MathsGame.destroy(); } catch (e) {}
    };
  }, [skillId]);

  return (
    <div style={{ position: "fixed", inset: 0 }}>
      <div ref={ref} style={{ position: "fixed", top: "7.5%", left: "7.5%", width: "85%", height: "85%" }} />
      <div ref={liveRef} aria-live="polite" role="status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }} />
      <Link
        to="/login"
        className="no-print"
        style={{
          position: "fixed", left: 14, bottom: 14, zIndex: 10,
          font: "600 13px/1 ui-rounded, 'SF Pro Rounded', Nunito, system-ui, sans-serif",
          color: "var(--on-surface-variant, #B4BAC8)",
          background: "var(--surface-container, #232733)",
          border: "1px solid var(--outline-variant, #3A3F4E)",
          borderRadius: 999, padding: "8px 14px", textDecoration: "none",
        }}
      >
        Sign in to save progress →
      </Link>
    </div>
  );
}
